import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, effect, inject, OnInit, signal, viewChild, WritableSignal } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { PopoverDirective, PopoverModule } from 'ngx-bootstrap/popover';
import { SaComponent } from 'smartauction-frontend-micro-library';
import { AccountManagerService } from '../../../services/account-manager/account-manager.service';
import { TranslocoDirective } from '@jsverse/transloco';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { AccountManagerResultDto, ConfigurationState, InventoryManagementState, LaunchDarklyService, UserProfileState } from 'remarketing-angular-library';
import { select, Store } from '@ngxs/store';
import { HeaderSearchExecutionService } from 'src/inventory-management/services/header-search-execution/header-search-execution.service';

@Component({
  selector: 'app-account-manager-selection',
  templateUrl: './account-manager-selection.component.html',
  styleUrl: './account-manager-selection.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [PopoverModule, FormsModule, TranslocoDirective, MatCheckboxModule],
  standalone: true
})
export class AccountManagerSelectionComponent extends SaComponent {
  protected readonly tenantName$ = select(ConfigurationState.tenantName);
  protected readonly inventoryManagementSearchCriteria$ = select(InventoryManagementState.searchCriteria);

  readonly popover$ = viewChild<PopoverDirective>('popover');

  private readonly accountManagerService = inject(AccountManagerService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly headerSearchExecutionService = inject(HeaderSearchExecutionService);
  private readonly launchDarklyService = inject(LaunchDarklyService);
  private readonly store = inject(Store);

  protected readonly selectedAccountManagers$: WritableSignal<AccountManagerResultDto[]> = signal([]);
  protected readonly visibleCount$: WritableSignal<number> = signal(10);
  protected readonly searchTerm$: WritableSignal<string> = signal('');

  protected readonly filteredAccountManagers$ = computed(() => {
    // Filter out already selected account managers
    let filtered = this.accountManagers.filter(am =>
      !this.selectedAccountManagers$().some(selected => selected.userUuid === am.userUuid)
    );

    if (this.searchTerm$()) {
      filtered = filtered.filter(am =>
        am.userName.toLowerCase().includes(this.searchTerm$())
      );
    }

    return filtered;
  });

  protected readonly visibleAccountManagers$ = computed(() => {
    const currentuserUuid = this.store.selectSnapshot(UserProfileState.userUuid);

    const sorted = [...this.filteredAccountManagers$()].sort((a, b) => {

      // if the current user is an account manager in the list, show them on top
      if (a.userUuid === currentuserUuid && b.userUuid !== currentuserUuid) return -1;
      if (b.userUuid === currentuserUuid && a.userUuid !== currentuserUuid) return 1;

      return a.userName.toLowerCase().localeCompare(b.userName.toLowerCase());
    });

    return sorted.slice(0, this.visibleCount$());
  });

  protected readonly sortedSelectedAccountManagers$ = computed(() => {
    const currentuserUuid = this.store.selectSnapshot(UserProfileState.userUuid);

    return [...this.selectedAccountManagers$()].sort((a, b) => {
      // if the current user is an account manager in the list, show them on top
      if (a.userUuid === currentuserUuid && b.userUuid !== currentuserUuid) return -1;
      if (b.userUuid === currentuserUuid && a.userUuid !== currentuserUuid) return 1;

      return a.userName.toLowerCase().localeCompare(b.userName.toLowerCase());
    });
  });

  private readonly itemsPerPage = 10;

  constructor() {
    super();
    effect(() => {
      if (this.accountManagerService.getAccountManagersCompleteSignal() === true) {
        this.setSelectedAccountManagers();
      }
    });
  }

  get maxSelectedItems(): number {
    return this.launchDarklyService.getFeatureFlag('sim-maxEmployeeSelections', 6);
  }

  get totalSelected(): number {
    return this.selectedAccountManagers$().length;
  }

  get accountManagers(): AccountManagerResultDto[] {
    return this.accountManagerService.accountManagers;
  }

  get hasMoreItems(): boolean {
    return this.filteredAccountManagers$().length > this.visibleCount$();
  }

  get canShowLess(): boolean {
    return this.visibleCount$() > this.itemsPerPage && this.filteredAccountManagers$().length >= this.itemsPerPage;
  }

  get shouldShowToggleButton(): boolean {
    return this.filteredAccountManagers$().length >= this.itemsPerPage;
  }

  get isMaxSelectionReached(): boolean {
    return this.selectedAccountManagers$().length >= this.maxSelectedItems;
  }

  get sortedSelectedAccountManagers(): AccountManagerResultDto[] {
    const currentuserUuid = this.store.selectSnapshot(UserProfileState.userUuid);

    return [...this.selectedAccountManagers$()].sort((a, b) => {
      // if the current user is an account manager in the list, show them on top
      if (a.userUuid === currentuserUuid && b.userUuid !== currentuserUuid) return -1;
      if (b.userUuid === currentuserUuid && a.userUuid !== currentuserUuid) return 1;

      return a.userName.toLowerCase().localeCompare(b.userName.toLowerCase());
    });
  }

  setSelectedAccountManagers(): void {
    const currentSelectedAccountManagers = this.inventoryManagementSearchCriteria$()?.filterByEmployee;
    if (currentSelectedAccountManagers) {
      this.selectedAccountManagers$.set(this.accountManagers.filter(accountManager => currentSelectedAccountManagers.includes(accountManager.userUuid)));
    } else {
      this.selectedAccountManagers$.set([]);
    }
    this.changeDetectorRef.markForCheck();
  }

  onShown(): void {
    this.searchTerm$.set('');
    this.visibleCount$.set(this.itemsPerPage);
  }

  applyAndSearch(): void {
    let employees = this.selectedAccountManagers$().map(accountManager => accountManager.userUuid);
    if (employees && employees.length > 0) {
      this.headerSearchExecutionService.setEmployeeFilter(employees);
    } else {
      this.headerSearchExecutionService.setEmployeeFilter(undefined);
    }
    this.popover$().hide();
  }

  isAccountManagerSelected(accountManager: AccountManagerResultDto): boolean {
    return this.selectedAccountManagers$().some(am => am.userUuid === accountManager.userUuid);
  }

  onCheckboxClick(event: MouseEvent, accountManager: AccountManagerResultDto): void {
    event.stopPropagation();
    this.toggleAccountManager(accountManager);
  }

  toggleAccountManager(accountManager: AccountManagerResultDto): void {
    const index = this.selectedAccountManagers$().findIndex(am => am.userUuid === accountManager.userUuid);
    if (index > -1) {
      this.selectedAccountManagers$.update((selectedAccountManagers) =>
        [ ...selectedAccountManagers.filter(am => am.userUuid !== accountManager.userUuid) ]
      );
    } else {
      this.selectedAccountManagers$.update((selectedAccountManagers) =>
        [...selectedAccountManagers, accountManager]
      );
    }
    this.changeDetectorRef.markForCheck();
  }

  search(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.searchTerm$.set(target.value.toLowerCase().trim());
    this.visibleCount$.set(this.itemsPerPage);
  }

  clearSearch(event: MouseEvent): void {
    event.stopPropagation();
    this.searchTerm$.set('');
    this.visibleCount$.set(this.itemsPerPage);
  }

  showMore(event: MouseEvent): void {
    event.stopPropagation();
    this.visibleCount$.update((visibleCount) => visibleCount += this.itemsPerPage);
  }

  showLess(event: MouseEvent): void {
    event.stopPropagation();
    this.visibleCount$.set(this.itemsPerPage);
  }
}
